import { useEffect, useMemo, useState } from "react";
import { BrandMark } from "@/shared/brand/BrandMark";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getPersistentCacheStatus,
  onPersistentCacheStatusChanged,
  type PersistentCacheStatus,
} from "../api/persistent-cache";
import { fetchSubscriptionEntitlement } from "../api/subscription";
import { fetchPublicConfig } from "../api/public-config";
import { GlobalAudioPlayer } from "../audio/GlobalAudioPlayer";
import {
  getAudioPlaybackState,
  onAudioPlaybackChanged,
  setAudioPlaybackRate,
} from "../audio/briefing-audio";
import { useAuth } from "../auth/AuthProvider";
import { publicWebCopyForLang, type PublicWebCopyKey } from "../i18n/public-web-copy";
import {
  getConnectionStatus,
  onConnectionStatusChanged,
  type ConnectionStatus,
} from "../net/connection-status";
import { rememberAppRoute, safeAppRouteTarget } from "../navigation/app-route-memory";
import { loadPreferences, onPreferencesChanged } from "../preferences/store";
import {
  getCloudSyncStatus,
  onCloudSyncStatusChanged,
  type CloudSyncStatus,
} from "../sync/cloud-sync";
import { useTheme } from "../theme/ThemeProvider";
import { Button } from "../ui/Button";
import { useGoogleTasksForegroundSync } from "../tasks/use-google-tasks-foreground-sync";
import { hasGoogleIntegrations } from "../google/google-integration-policy.mjs";
import { SettingsIcon } from "./SettingsIcon";
import "./AppLayout.css";

const PRIMARY_NAV = [
  { to: "/app/brief", copyKey: "home.brief", icon: "brief" },
  { to: "/app/home?surface=dashboard", copyKey: "home.dashboard", icon: "overview" },
  { to: "/app/news", copyKey: "layout.news", icon: "news" },
  { to: "/app/saved", copyKey: "layout.saved", icon: "saved" },
] as const;

const MOBILE_NAV = PRIMARY_NAV;

const MOBILE_HEADER_NAV = [
  { to: "/app/settings", copyKey: "layout.settings", icon: "settings" },
] as const;

type AppIconName =
  | (typeof PRIMARY_NAV)[number]["icon"]
  | (typeof MOBILE_HEADER_NAV)[number]["icon"];

type Copy = (key: PublicWebCopyKey, vars?: Record<string, string | number>) => string;

function primaryDestination(pathname: string, search: string): AppIconName | null {
  if (pathname === "/app/brief") return "brief";
  if (
    (pathname === "/app/home" && new URLSearchParams(search).get("surface") === "dashboard") ||
    ["/app/markets", "/app/tasks", "/app/calendar"].some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    )
  )
    return "overview";
  if (pathname === "/app/news" || pathname.startsWith("/app/news/")) return "news";
  if (pathname === "/app/saved" || pathname.startsWith("/app/saved/")) return "saved";
  return null;
}

function displayTierLabel(value: string | null | undefined, copy: Copy): string {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  if (!normalized || normalized === "FREE") return copy("layout.tierFree");
  if (normalized.includes("TRIAL")) return copy("layout.tierTrial");
  if (normalized.includes("PREMIUM")) return copy("layout.tierPremium");
  return normalized
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function mobileSurfaceTitle(pathname: string, copy: Copy): string {
  if (pathname === "/app/home") return copy("home.dashboard");
  if (pathname.startsWith("/app/news")) return copy("layout.news");
  if (pathname.startsWith("/app/markets")) return copy("layout.stocks");
  if (pathname.startsWith("/app/tasks")) return copy("layout.tasks");
  if (pathname.startsWith("/app/saved")) return copy("layout.saved");
  if (pathname.startsWith("/app/calendar")) return copy("layout.calendar");
  if (pathname.startsWith("/app/settings")) return copy("layout.settings");
  if (pathname.startsWith("/app/reader")) return copy("layout.article");
  return copy("brief.eyebrow");
}

function AppIcon({ name }: { name: AppIconName }) {
  if (name === "settings") return <SettingsIcon className="app-nav-icon" />;

  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 2,
  };
  return (
    <svg className="app-nav-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {name === "brief" ? (
        <>
          <path {...common} d="M3 11.5 12 4l9 7.5" />
          <path {...common} d="M5.5 10.5V20h13v-9.5" />
          <path {...common} d="M9.5 20v-6h5v6" />
        </>
      ) : null}
      {name === "overview" ? (
        <>
          <rect {...common} x="4" y="4" width="6" height="6" rx="1.4" />
          <rect {...common} x="14" y="4" width="6" height="6" rx="1.4" />
          <rect {...common} x="4" y="14" width="6" height="6" rx="1.4" />
          <rect {...common} x="14" y="14" width="6" height="6" rx="1.4" />
        </>
      ) : null}
      {name === "news" ? (
        <>
          <path {...common} d="M5 5h14v14H5z" />
          <path {...common} d="M8 9h8M8 13h8M8 17h5" />
        </>
      ) : null}
      {name === "saved" ? <path {...common} d="M6.5 4.5h11v16L12 17l-5.5 3.5v-16Z" /> : null}
    </svg>
  );
}

export function AppLayout() {
  const { data: publicConfig } = useQuery({
    queryKey: ["public-config"],
    queryFn: fetchPublicConfig,
  });
  const googleIntegrationsEnabled = hasGoogleIntegrations(publicConfig);
  useGoogleTasksForegroundSync(googleIntegrationsEnabled);
  const { user, signedIn, signOut } = useAuth();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [audioPlayerActive, setAudioPlayerActive] = useState(
    () => getAudioPlaybackState().phase !== "idle",
  );
  const [syncStatus, setSyncStatus] = useState<CloudSyncStatus>(() => getCloudSyncStatus());
  const [cacheStatus, setCacheStatus] = useState<PersistentCacheStatus>(() =>
    getPersistentCacheStatus(),
  );
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(() =>
    getConnectionStatus(),
  );
  const [layoutLang, setLayoutLang] = useState(() => loadPreferences().lang);
  const copy = useMemo(() => publicWebCopyForLang(layoutLang), [layoutLang]);
  const { data: entitlement } = useQuery({
    queryKey: ["subscription-entitlement"],
    queryFn: fetchSubscriptionEntitlement,
    staleTime: 60_000,
    retry: 1,
  });
  const tierLabel = entitlement?.hasPremiumAccess
    ? entitlement.trialActive
      ? copy("layout.tierTrial")
      : copy("layout.tierPremium")
    : displayTierLabel(user?.subscriptionTier, copy);
  const homeSurfaceRoute = location.pathname === "/app/home" || location.pathname === "/app/brief";
  const selectedDestination = primaryDestination(location.pathname, location.search);
  const audioPlayerVisible = audioPlayerActive;
  const layoutClassName = [
    "app-layout",
    homeSurfaceRoute ? "app-layout--home-surface" : "",
    audioPlayerVisible ? "app-layout--audio-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    rememberAppRoute(`${location.pathname}${location.search}${location.hash}`);
  }, [location.hash, location.pathname, location.search]);

  useEffect(
    () =>
      onAudioPlaybackChanged(() => {
        setAudioPlayerActive(getAudioPlaybackState().phase !== "idle");
      }),
    [],
  );

  useEffect(() => onCloudSyncStatusChanged(() => setSyncStatus(getCloudSyncStatus())), []);

  useEffect(
    () => onPersistentCacheStatusChanged(() => setCacheStatus(getPersistentCacheStatus())),
    [],
  );

  useEffect(() => onConnectionStatusChanged(() => setConnectionStatus(getConnectionStatus())), []);

  useEffect(() => {
    setAudioPlaybackRate(loadPreferences().audioPlaybackSpeed);
    return onPreferencesChanged(() => {
      const preferences = loadPreferences();
      setLayoutLang(preferences.lang);
      setAudioPlaybackRate(preferences.audioPlaybackSpeed);
    });
  }, []);

  return (
    <div className={layoutClassName}>
      <a className="skip-link" href="#main-content">
        {copy("layout.skipMain")}
      </a>
      <aside className="app-side" aria-label={copy("layout.appNavigation")}>
        <Link className="app-side-brand" to="/app/brief" aria-label={copy("brief.eyebrow")}>
          <BrandMark />
        </Link>
        <nav className="app-side-nav" aria-label={copy("layout.primaryNavigation")}>
          {PRIMARY_NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              aria-current={item.icon === selectedDestination ? "page" : undefined}
              className={`app-side-link${item.icon === selectedDestination ? " app-side-link--active" : ""}`}
            >
              <AppIcon name={item.icon} />
              {copy(item.copyKey)}
            </Link>
          ))}
        </nav>
        <nav
          className="app-side-nav app-side-nav--utility"
          aria-label={copy("layout.librarySettings")}
        >
          {MOBILE_HEADER_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `app-side-link${isActive ? " app-side-link--active" : ""}`
              }
            >
              <AppIcon name={item.icon} />
              {copy(item.copyKey)}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="app-body">
        <header className="app-top">
          <div className="app-top-meta">
            <div className="app-brand-pill">
              <BrandMark />
              <strong>Today&apos;s Brief</strong>
            </div>
            <strong className="app-mobile-title">
              {mobileSurfaceTitle(location.pathname, copy)}
            </strong>
            <span className="app-greeting">
              {user?.displayName
                ? copy("layout.greetingNamed", { name: user.displayName })
                : copy("layout.yourBrief")}
            </span>
            <div className="app-top-badges">
              <NavLink to="/app/settings/subscription" className="app-tier">
                {tierLabel}
              </NavLink>
              {syncStatus.phase !== "idle" ? (
                <span className={`app-sync app-sync--${syncStatus.phase}`}>
                  {syncStatus.phase === "pulling"
                    ? copy("layout.syncRestoring")
                    : syncStatus.phase === "pushing"
                      ? copy("layout.syncSaving")
                      : syncStatus.phase === "synced"
                        ? copy("layout.syncSynced")
                        : copy("layout.syncLocal")}
                </span>
              ) : null}
              {cacheStatus.phase === "fallback" ? (
                <span
                  className="app-cache app-cache--fallback"
                  title={copy("layout.savedDataReason", { path: cacheStatus.path })}
                >
                  {copy("layout.savedData")}
                </span>
              ) : null}
              {!connectionStatus.online ? (
                <span className="app-connection app-connection--offline" role="status">
                  {copy("layout.offline")}
                </span>
              ) : null}
            </div>
          </div>
          <div className="app-top-actions">
            <nav className="app-top-shortcuts" aria-label={copy("layout.savedSettings")}>
              {MOBILE_HEADER_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-label={copy(item.copyKey)}
                  title={copy(item.copyKey)}
                  className={({ isActive }) =>
                    `app-top-shortcut${isActive ? " app-top-shortcut--active" : ""}`
                  }
                >
                  <AppIcon name={item.icon} />
                  <span className="visually-hidden">{copy(item.copyKey)}</span>
                </NavLink>
              ))}
            </nav>
            <label className="app-theme-label">
              <span className="visually-hidden">{copy("layout.theme")}</span>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as typeof mode)}
                aria-label={copy("layout.theme")}
              >
                <option value="system">{copy("layout.themeSystem")}</option>
                <option value="light">{copy("layout.themeLight")}</option>
                <option value="dark">{copy("layout.themeDark")}</option>
              </select>
            </label>
            {signedIn ? <Button
              variant="ghost"
              onClick={() => {
                void signOut().then(() => navigate("/"));
              }}
            >
              {copy("layout.signOut")}
            </Button> : <Link className="tb-btn tb-btn--ghost" to="/auth/sign-in"
              state={{ from: safeAppRouteTarget(`${location.pathname}${location.search}${location.hash}`, "/app/brief") }}>
              {copy("marketing.layout.signIn")}
            </Link>}
          </div>
        </header>
        <main id="main-content" className="app-main" tabIndex={-1} data-route-focus-target>
          <Outlet />
        </main>
        <GlobalAudioPlayer lang={layoutLang} />
      </div>
      <nav className="app-bottom" aria-label={copy("layout.mobileNavigation")}>
        {MOBILE_NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            aria-current={item.icon === selectedDestination ? "page" : undefined}
            className={`app-bottom-link${item.icon === selectedDestination ? " app-bottom-link--active" : ""}`}
          >
            <AppIcon name={item.icon} />
            {copy(item.copyKey)}
          </Link>
        ))}
      </nav>
    </div>
  );
}
